import {
	API_PRODUCT_CATEGORY,
	API_TRANSPORT_SUBCATEGORY,
	ApiAccommodationProduct,
	ApiActivity,
	ApiActivityProduct,
	ApiOtherTransportProduct,
	ApiPrice,
	ApiProduct,
	ApiProvider,
	ApiRentalTransportProduct,
	ApiTransportProduct,
	ApiTripProduct,
} from '~/passporter-services/product/apiType'
import Block from '~/models/Block'
import Product, {
	ACCOMMODATION_SUBCATEGORY,
	AccommodationProduct,
	Activity,
	ActivityProduct,
	PRODUCT_CATEGORY,
	ProductFilterLanguage,
	PublicTransportProduct,
	RentalTransportProduct,
	TRANSPORT_SUBCATEGORY,
	TransportProduct,
	TripProduct,
} from '~/models/Product'
import Price from '~/models/Price'
import Currency from '~/models/Currency'
import { Provider } from '~/models/Provider'

export class ProductEntityMapper {
	static toBlock(block: any): Block<any> {
		const content = this.toProducts(block.content ?? [])
		return {
			id: block.id,
			subtitle: block.subtitle,
			title: block.title,
			header: block.header,
			description: block.description,
			image: block.image,
			type: block.type,
			button: block.button,
			link: block.link,
			content,
		}
	}

	static toProduct(apiProduct: ApiProduct): Product {
		const provider = this.getProvider(apiProduct.provider)
		const base: Product = {
			id: apiProduct.id,
			title: apiProduct.title,
			description: apiProduct.description,
			url: apiProduct.external_url,
			coverUrls: apiProduct.cover_urls,
			price: this.getProductPrice(apiProduct.prices),
			category: PRODUCT_CATEGORY.TRIP,
			isFavorite: apiProduct.is_favorite,
			languages: apiProduct.languages as ProductFilterLanguage[],
			provider,
		}
		return ProductEntityMapper.productMappers[apiProduct.category](base, apiProduct)
	}

	static toProducts(products: ApiProduct[]): Product[] {
		return products.map((product: ApiProduct) => this.toProduct(product))
	}

	/** @deprecated Used only by DestinationRepository.getActivities() for the microsite activities endpoint. */
	static toActivity(apiActivity: ApiActivity | ApiActivityProduct): Activity {
		const activity = apiActivity as ApiActivity
		return {
			id: activity.id,
			title: activity.title,
			description: activity.description,
			category: PRODUCT_CATEGORY.ACTIVITY,
			url: activity.url,
			coverUrls: [activity.photo],
			price: this.getPrice(activity.price, activity.currency),
			duration: activity.duration,
			reviews: activity.reviews,
			score: activity.score,
			location: activity.location,
		}
	}

	/** @deprecated Used only by DestinationRepository.getActivities() for the microsite activities endpoint. */
	static toActivities(activities: Array<ApiActivity | ApiActivityProduct>): Activity[] {
		return activities.map((activity) => this.toActivity(activity))
	}

	private static productMappers: Record<
		API_PRODUCT_CATEGORY,
		(base: Product, apiProduct: ApiProduct) => Product
	> = {
		[PRODUCT_CATEGORY.TRIP]: (base, api) =>
			ProductEntityMapper.toTripProduct(base, api as ApiTripProduct),
		[PRODUCT_CATEGORY.ACCOMMODATION]: (base, api) =>
			ProductEntityMapper.toAccommodationProduct(base, api as ApiAccommodationProduct),
		[PRODUCT_CATEGORY.ACTIVITY]: (base, api) =>
			ProductEntityMapper.toActivityProduct(base, api as ApiActivityProduct),
		[PRODUCT_CATEGORY.TRANSPORT]: (base, api) =>
			ProductEntityMapper.toTransportProduct(base, api as ApiTransportProduct),
	}

	private static toTripProduct(base: Product, apiProduct: ApiTripProduct): TripProduct {
		const product: TripProduct = {
			...base,
			category: PRODUCT_CATEGORY.TRIP,
			data: apiProduct.data,
			markers: apiProduct.markers?.map((marker) => ({
				label: marker.label,
				location: marker.location,
			})),
			itinerary: apiProduct.itinerary?.map((day) => ({
				day: day.day,
				destinations: day.destinations,
				products: day.products ? this.toProducts(day.products) : [],
			})),
		}
		return product
	}

	private static toAccommodationProduct(
		base: Product,
		apiProduct: ApiAccommodationProduct
	): AccommodationProduct {
		const product: AccommodationProduct = {
			...base,
			category: PRODUCT_CATEGORY.ACCOMMODATION,
			subcategory: apiProduct.subcategory as ACCOMMODATION_SUBCATEGORY | undefined,
			data: apiProduct.data,
		}
		return product
	}

	private static toActivityProduct(base: Product, apiProduct: ApiActivityProduct): ActivityProduct {
		const product: ActivityProduct = {
			...base,
			category: PRODUCT_CATEGORY.ACTIVITY,
			data: {
				startDate: apiProduct.data?.start_date ? new Date(apiProduct.data!.start_date) : undefined,
				duration: apiProduct.data?.duration,
				freeCancellation: apiProduct.data?.free_cancellation_policy,
				accessibilityWheelchair: apiProduct.data?.accessibility_wheelchair,
				included: apiProduct.data?.included,
				notIncluded: apiProduct.data?.not_included,
				meetingPoint: apiProduct.data?.meeting_point,
				advanceMinutes: apiProduct.data?.advance_minutes,
			},
		}
		return product
	}

	private static toTransportProduct(
		base: Product,
		apiProduct: ApiTransportProduct
	): TransportProduct {
		if (apiProduct.subcategory === API_TRANSPORT_SUBCATEGORY.RENTAL) {
			return ProductEntityMapper.toRentalTransportProduct(
				base,
				apiProduct as ApiRentalTransportProduct
			)
		}
		return ProductEntityMapper.toOtherTransportProduct(base, apiProduct as ApiOtherTransportProduct)
	}

	private static toRentalTransportProduct(
		base: Product,
		apiProduct: ApiRentalTransportProduct
	): RentalTransportProduct {
		const product: RentalTransportProduct = {
			...base,
			category: PRODUCT_CATEGORY.TRANSPORT,
			subcategory: TRANSPORT_SUBCATEGORY.RENTAL,
			data: {
				company: apiProduct.data?.company,
				pickupLocation: apiProduct.data?.pickup_location,
				dropoffLocation: apiProduct.data?.dropoff_location,
			},
		}
		if (apiProduct.data)
			product.data = {
				company: apiProduct.data.company,
				pickupLocation: apiProduct.data.pickup_location,
				dropoffLocation: apiProduct.data.dropoff_location,
			}
		return product
	}

	private static toOtherTransportProduct(
		base: Product,
		apiProduct: ApiOtherTransportProduct
	): PublicTransportProduct {
		const product: PublicTransportProduct = {
			...base,
			category: PRODUCT_CATEGORY.TRANSPORT,
			subcategory: apiProduct.subcategory as any,
			data: {
				company: apiProduct.data?.company,
				duration: apiProduct.data?.duration,
				baggageInfo: apiProduct.data?.baggage_info,
				departure: apiProduct.data?.departure ? new Date(apiProduct.data.departure) : undefined,
				arrival: apiProduct.data?.arrival ? new Date(apiProduct.data.arrival) : undefined,
				originDestinationCode: apiProduct.data?.origin_destination_code,
				targetDestinationCode: apiProduct.data?.target_destination_code,
				originAirportName: apiProduct.data?.origin_airport_name,
				targetAirportName: apiProduct.data?.target_airport_name,
				dayDifference: apiProduct.data?.day_difference,
				numSegments: apiProduct.data?.num_segments,
			},
		}
		return product
	}

	private static getPrice(priceValue: number, priceCurrency: string): Price {
		const currency = new Currency({ code: priceCurrency })
		const price = new Price({
			currency,
			value: priceValue,
		})
		return price
	}

	private static getProductPrice(apiPrices?: ApiPrice[]): Price | undefined {
		if (!apiPrices?.length) return
		const perPerson = apiPrices.find((price) => price.type === 'per_person')
		const final = perPerson || apiPrices[0]!

		return this.getPrice(final.price, final.currency)
	}

	private static getProvider(apiProvider?: ApiProvider): Provider | undefined {
		if (!apiProvider) return
		return {
			id: apiProvider.id,
			name: apiProvider.name,
			logo: apiProvider.logo,
		}
	}
}
